/**
 * `/ocr-tool` — one scanned PDF in, one Word document out, nothing kept.
 *
 * The standalone OCR tool for the years of paper case files that exist only as
 * scans. The officer picks what the document is, types the charged officer's
 * name, uploads one PDF, and downloads a .docx named after those two fields to
 * proofread in Word — before the paper ever enters a case.
 *
 * Three postures this screen holds:
 *
 * - **The two fields come first.** The file input stays disabled until both
 *   are filled, because they name the download and there is no later moment to
 *   ask.
 * - **The type list is the process definition's own rail** (`GET /process`),
 *   plus the same free-text "Other / supporting document" class the papers
 *   screen uses. No second list, no hardcoded title (stop list 6).
 * - **Warnings come before the download.** A page that failed OCR, came back
 *   empty, or degenerated into description or a loop is listed on screen
 *   first — otherwise the officer downloads blind and finds out in Word.
 *
 * Downloading deletes the job server-side; nothing about the file, the name or
 * the type survives anywhere.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, ApiFailure } from '../api/client'
import type { OcrToolJobView } from '../api/client'
import { ErrorEnvelope } from '../components/bits'

const OTHER = '__other__'

export function OcrTool() {
  const [type, setType] = useState('')
  const [description, setDescription] = useState('')
  const [officerName, setOfficerName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [downloaded, setDownloaded] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  // Mirrors `previewUrl` so unmount can revoke the *current* object URL — an
  // effect closing over the state would only ever see the initial null.
  const previewRef = useRef<string | null>(null)
  previewRef.current = previewUrl

  // The pinned rail of the published process — never a literal list here.
  const process = useQuery({ queryKey: ['process'], queryFn: () => api.getProcess() })
  const types = useMemo(
    () =>
      (process.data?.documents ?? []).map((doc) => ({
        value: doc.id,
        label: `${doc.id} · ${doc.short_title ?? doc.title}`,
      })),
    [process.data],
  )

  const fieldsReady =
    officerName.trim().length > 0 &&
    (type === OTHER ? description.trim().length > 0 : type.length > 0)

  const start = useMutation({
    mutationFn: (upload: File) =>
      api.createOcrToolJob(upload, {
        officer_name: officerName.trim(),
        document_type: type && type !== OTHER ? type : undefined,
        other_description: type === OTHER ? description.trim() : undefined,
      }),
    onSuccess: (job) => {
      setJobId(job.id)
      setDownloaded(false)
    },
  })

  const job = useQuery({
    queryKey: ['ocr-tool', jobId],
    queryFn: () => api.getOcrToolJob(jobId as string),
    enabled: jobId !== null && !downloaded,
    refetchInterval: (query) => {
      const state = query.state.data?.state
      return state === 'QUEUED' || state === 'READING' ? 2500 : false
    },
    retry: (count, failure) =>
      failure instanceof ApiFailure && failure.status === 404 ? false : count < 2,
  })

  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current)
    },
    [],
  )

  function pick(selected: File | null) {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setFile(selected)
    setPreviewUrl(selected ? URL.createObjectURL(selected) : null)
  }

  function reset(discard: boolean) {
    if (discard && jobId) void api.discardOcrToolJob(jobId).catch(() => undefined)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setType('')
    setDescription('')
    setOfficerName('')
    setFile(null)
    setPreviewUrl(null)
    setJobId(null)
    setDownloaded(false)
    start.reset()
    if (fileInput.current) fileInput.current.value = ''
  }

  return (
    <div className="page">
      <header>
        <h1>OCR to Word</h1>
        <p className="small muted">
          One scanned PDF becomes an editable Word document, to proofread before the paper
          enters a case. This tool keeps nothing: once you download, no record of the file, the
          name or the type survives anywhere.
        </p>
      </header>

      {jobId === null ? (
        <section className="card">
          <header>
            <span>What is being read</span>
            <span className="small muted">
              both fields name the Word file, so they come first
            </span>
          </header>
          <div className="body">
            <div className="row">
              <div className="field">
                <label htmlFor="ocr-type">Document type</label>
                <select
                  id="ocr-type"
                  aria-label="Document type"
                  value={type}
                  onChange={(event) => setType(event.target.value)}
                >
                  <option value="">— pick what the document is —</option>
                  {types.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                  <option value={OTHER}>Other / supporting document</option>
                </select>
              </div>
              {type === OTHER ? (
                <div className="field">
                  <label htmlFor="ocr-description">What it is</label>
                  <input
                    id="ocr-description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="what the office calls it"
                  />
                </div>
              ) : null}
              <div className="field">
                <label htmlFor="ocr-officer">Charged officer&rsquo;s name</label>
                <input
                  id="ocr-officer"
                  value={officerName}
                  onChange={(event) => setOfficerName(event.target.value)}
                  placeholder="as it should appear in the filename"
                />
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label htmlFor="ocr-file">The scan — one PDF</label>
                <input
                  id="ocr-file"
                  ref={fileInput}
                  type="file"
                  accept="application/pdf,.pdf"
                  disabled={!fieldsReady}
                  onChange={(event) => pick(event.target.files?.[0] ?? null)}
                />
                {!fieldsReady ? (
                  <p className="small muted">
                    Fill the two fields above first — they name the Word file.
                  </p>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              disabled={!fieldsReady || !file || start.isPending}
              onClick={() => file && start.mutate(file)}
            >
              {start.isPending ? 'Uploading…' : 'Read the scan'}
            </button>
            <ErrorEnvelope error={start.error} />
          </div>
        </section>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 16 }}>
          <section className="card">
            <header>
              <span>The scan</span>
              <span className="small mono muted">{file?.name}</span>
            </header>
            <div className="body">
              {previewUrl ? (
                <iframe
                  title="Uploaded scan"
                  src={previewUrl}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              ) : (
                <p className="small muted">The preview is gone — the upload was discarded.</p>
              )}
            </div>
          </section>
          <JobPanel
            job={job.data}
            missing={job.error instanceof ApiFailure && job.error.status === 404}
            jobId={jobId}
            downloaded={downloaded}
            onDownloaded={() => setDownloaded(true)}
            onReset={reset}
          />
        </div>
      )}
    </div>
  )
}

function JobPanel({
  job,
  missing,
  jobId,
  downloaded,
  onDownloaded,
  onReset,
}: {
  job: OcrToolJobView | undefined
  missing: boolean
  jobId: string
  downloaded: boolean
  onDownloaded: () => void
  onReset: (discard: boolean) => void
}) {
  if (downloaded) {
    return (
      <section className="card">
        <header>
          <span>Done</span>
        </header>
        <div className="body">
          <p className="small">
            The Word file is with you. The upload, the name and the type have been discarded —
            nothing about this document survives here.
          </p>
          <button type="button" onClick={() => onReset(false)}>
            Read another scan
          </button>
        </div>
      </section>
    )
  }

  if (missing) {
    return (
      <section className="card">
        <header>
          <span>Gone</span>
        </header>
        <div className="body">
          <p className="small muted">
            This job no longer exists — it expired or was already downloaded. Nothing was kept.
          </p>
          <button type="button" onClick={() => onReset(false)}>
            Start again
          </button>
        </div>
      </section>
    )
  }

  if (!job) {
    return (
      <section className="card">
        <header>
          <span>Reading…</span>
        </header>
        <div className="body">
          <p className="small muted">Waiting for the first word from the reader.</p>
        </div>
      </section>
    )
  }

  const running = job.state === 'QUEUED' || job.state === 'READING'
  const warnings = job.warnings ?? []
  return (
    <ReadyPanel
      job={job}
      running={running}
      warnings={warnings}
      jobId={jobId}
      onDownloaded={onDownloaded}
      onReset={onReset}
    />
  )
}

function ReadyPanel({
  job,
  running,
  warnings,
  jobId,
  onDownloaded,
  onReset,
}: {
  job: OcrToolJobView
  running: boolean
  warnings: NonNullable<OcrToolJobView['warnings']>
  jobId: string
  onDownloaded: () => void
  onReset: (discard: boolean) => void
}) {
  // Fetch, save, and only then confirm. "Done" on an anchor's click would
  // assert delivery nobody observed: a dropped connection or an expired job
  // would show a success card over a download that never arrived — and the
  // server must not delete its only copy until the bytes are known saved.
  const download = useMutation({
    mutationFn: async () => {
      const { blob, filename } = await api.downloadOcrToolDocument(jobId)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      anchor.click()
      URL.revokeObjectURL(url)
    },
    onSuccess: async () => {
      await api.discardOcrToolJob(jobId).catch(() => undefined)
      onDownloaded()
    },
  })

  return (
    <section className="card">
      <header>
        <span>
          {running
            ? 'Reading the scan…'
            : job.state === 'FAILED'
              ? 'The scan could not be read'
              : 'Ready'}
        </span>
        <span className="small mono muted">
          {job.pages_done} / {job.pages_total} pages
        </span>
      </header>
      <div className="body">
        {/* **Waiting and unattended are different waits** (walkthrough entry 27,
            fix 2). "This screen updates itself; the reading continues if you
            look away" is true of a job somebody is reading and a lie about a
            job nobody has picked up — and two of those sat for forty-two
            minutes. Only the server can tell them apart, so it says which. */}
        {running && job.service_unavailable ? (
          <p className="small warn-text" data-testid="ocr-service-unavailable">
            <strong>Nothing is reading this.</strong> {job.service_unavailable}
          </p>
        ) : running ? (
          <p className="small muted">
            {job.chunks_total > 1
              ? `${job.chunks_total} parts at Doc AI's ten-page cap — about ` +
                `${Math.max(1, Math.ceil(job.estimated_minutes ?? 1))} minute(s) at its pace. `
              : ''}
            This screen updates itself; the reading continues if you look away.
          </p>
        ) : null}

        {job.error ? <p className="small error">{job.error}</p> : null}

        {!running && warnings.length > 0 ? (
          <div data-testid="ocr-warnings">
            <p className="small">
              <strong>
                {warnings.length} page{warnings.length === 1 ? '' : 's'} need your eye
              </strong>{' '}
              before you trust the Word file. Each carries the same notice inside the document.
            </p>
            <ul className="small">
              {warnings.map((warning) => (
                <li key={warning.page_no}>
                  <span className="mono">p. {warning.page_no}</span> — {warning.note}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {!running && warnings.length === 0 && job.state === 'COMPLETED' ? (
          <p className="small muted">Every page came back with text and none looked wrong.</p>
        ) : null}

        {(job.problems ?? []).map((problem) => (
          <p key={problem} className="small muted">
            {problem}
          </p>
        ))}

        {job.document_ready ? (
          <p>
            <button
              type="button"
              className="btn"
              data-testid="ocr-download"
              disabled={download.isPending}
              onClick={() => download.mutate()}
            >
              {download.isPending ? 'Fetching…' : 'Download the Word file'}
            </button>
          </p>
        ) : null}
        {job.document_ready ? (
          <p className="small muted">
            Once the file is saved, everything this tool holds is discarded.
          </p>
        ) : null}
        <ErrorEnvelope error={download.error} />

        <button type="button" className="quiet" onClick={() => onReset(true)}>
          Discard without downloading
        </button>
      </div>
    </section>
  )
}
